import { HTMLProps } from 'react';
import { useTranslation } from 'react-i18next';
import { MONTHS_IN_YEAR } from '../../../../config/constants';
import { toPrice, toPriceRounded } from '../../../../utils/parsers';
import { useIsJointCalculation } from '../../hooks/usePensionDate';
import { useShortageOrSurplus } from '../../hooks/useSurplusCorrected';
import { ShortageWarning, SurplusNoWarning } from '../RetirementWarning';
import { usePensionDateOrder } from './ShortageTopText';

export const OverageTopText: React.FC<{
    goal: number, notFullfilledMissingAOW?: number
} & HTMLProps<HTMLDivElement>> = ({ goal, notFullfilledMissingAOW }) => {
    const { t } = useTranslation('pension');
    const pensionDateOrder = usePensionDateOrder();
    const calculatedTogether = useIsJointCalculation();
    const shortageOrSurplus = useShortageOrSurplus();
    const notCompensatedAow = notFullfilledMissingAOW;
    const hasNotCompensatedAow = !!(notCompensatedAow && notCompensatedAow > 0)

    const selfSurplusAsPrice = toPriceRounded(shortageOrSurplus.selfShortageOrSurplusPerYear / MONTHS_IN_YEAR)
    const partnerSurplusAsPrice = toPriceRounded(shortageOrSurplus.partnerShortageOrSurplusPerYear / MONTHS_IN_YEAR)

    const getWithPartnerText = () => {
        // Both have a surplus but not all AOW is compensated through the surplus
        if (hasNotCompensatedAow && notCompensatedAow){
            const incomeExtraRequiredAsPrice = toPriceRounded(notCompensatedAow)
            
            // Self and Partner have the same penion date
            if (pensionDateOrder === "samePensionDate"){
                return t("pension-warning-joined-both-surplus-but-aow-shortage-same-pension-date", {
                    surplus: selfSurplusAsPrice,
                    aowText: t('pension-warning-aow-missing-joined-surplus'),
                    incomeExtraRequired: incomeExtraRequiredAsPrice,
                }) + t('pension-cokctail-fill-screen-results-other-options-text')
            }

            // Self goes earlier with pension than partner
            if (pensionDateOrder === "selfEarlierPensionDate"){
                return t("pension-warning-joined-both-surplus-but-aow-shortage-self-earlier-with-pension", {
                    selfSurplus: selfSurplusAsPrice,
                    partnerSurplus: partnerSurplusAsPrice,
                    aowText: t('pension-warning-aow-missing-joined-surplus'),
                    incomeExtraRequired: incomeExtraRequiredAsPrice,
                }) + t('pension-cokctail-fill-screen-results-other-options-text')
            }

            // Partner goes earlier with pension than self
            return t("pension-warning-joined-both-surplus-but-aow-shortage-partner-earlier-with-pension", {
                selfSurplus: selfSurplusAsPrice,
                partnerSurplus:partnerSurplusAsPrice,
                aowText: t('pension-warning-aow-missing-joined-surplus'),
                incomeExtraRequired: incomeExtraRequiredAsPrice,
            }) + t('pension-cokctail-fill-screen-results-other-options-text')
        }

        // Both have a surplus and all AOW is compensated through the surplus or no AOW is missed
        return t("pension-cocktail-fill-screen-results-overage-text", {
            incomeRequired: toPrice(goal, {
                onlyFullEuros: true,
                withCurrencySymbol: true,
            })
        })
    };

    const getSingleText = () => {
        // Self has an surplus but not all AOW is compensated through the surplus
        if (hasNotCompensatedAow && notCompensatedAow){
            return t("pension-cocktail-fill-screen-results-overage-single-aow-shortage-text", {
                selfSurplus: selfSurplusAsPrice,
                incomeRequired: toPriceRounded(notCompensatedAow),
                aowText: t('pension-warning-aow-missing-single-surplus')
            })
            + t('pension-cokctail-fill-screen-results-other-options-text');
        }

        // Self has an surplus and all AOW is compensated through the surplus
        return t("pension-cocktail-fill-screen-results-overage-text", {
            incomeRequired: toPriceRounded(goal)
        })
    }

    const getText = () => {
        return calculatedTogether
            ? getWithPartnerText()
            : getSingleText();
    }

    return hasNotCompensatedAow
        ? <ShortageWarning text={getText()}/>
        : <SurplusNoWarning text={getText()}/>;
};